// src/pages/Admin/ActivationFunnel.js
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import adminApi from '../../services/adminApi';
import styles from './OnboardingInsights.module.css';
import funnelStyles from './ActivationFunnel.module.css';
import TopMoviesFunnel from './TopMoviesFunnel';
import RankingsFunnel from './RankingsFunnel';
import {
    AndroidDeviceBreakdown,
    ClassificationSummary,
    CohortFilterBar,
    DiagnosticSummaryCards,
    DiagnosticUsersList,
    ErrorSignatures,
    OnboardingJourney,
    OnboardingStepFunnel,
    QuizHandoff,
    TitleSelectionOutcomes,
    filtersFromSearchParams,
} from './OnboardingDiagnosticsPanel';

const RANGES = [
    { value: '7d', label: '7 days' },
    { value: '30d', label: '30 days' },
    { value: '90d', label: '90 days' },
    { value: 'all', label: 'All time' },
];

const BREAKDOWNS = [
    { value: '', label: 'Overall' },
    { value: 'method', label: 'Signup method' },
    { value: 'platform', label: 'Platform' },
    { value: 'source', label: 'Source' },
    { value: 'app_version', label: 'App build' },
    { value: 'manufacturer', label: 'Manufacturer' },
    { value: 'os_version', label: 'OS version' },
    { value: 'install_type', label: 'Install type' },
];

export default function ActivationFunnel() {
    const [range, setRange] = useState('30d');
    const [by, setBy] = useState('');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Cross-filters live in the URL so a cohort view (e.g. Android + Google) can be shared.
    const [searchParams, setSearchParams] = useSearchParams();
    const filterKey = searchParams.toString();
    const filters = useMemo(() => filtersFromSearchParams(new URLSearchParams(filterKey)), [filterKey]);
    const [diag, setDiag] = useState(null);
    const [diagError, setDiagError] = useState(null);
    const [drill, setDrill] = useState(null);
    const [journeyUser, setJourneyUser] = useState(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        adminApi.getActivationFunnel({ range, ...filters, ...(by ? { by } : {}) })
            .then((res) => { if (!cancelled) setData(res); })
            .catch((err) => { if (!cancelled) setError(err.message || 'Failed to load the activation funnel'); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [range, by, filters]);

    useEffect(() => {
        let cancelled = false;
        setDiagError(null);
        setDrill(null);
        adminApi.getOnboardingDiagnostics({ range, ...filters })
            .then((res) => { if (!cancelled) setDiag(res); })
            .catch((err) => { if (!cancelled) setDiagError(err.message || 'Failed to load onboarding diagnostics'); });
        return () => { cancelled = true; };
    }, [range, filters]);

    const updateFilters = (next) => setSearchParams(next, { replace: true });

    const sourceAvailable = !!data?.dimensions?.source;

    return (
        <div className={styles.page}>
            <div className={styles.header}>
                <div>
                    <h1 className={styles.pageTitle}>Activation Funnel</h1>
                    <p className={styles.subtitle}>
                        Signup → first coin for users who signed up in the selected window.
                        {data?.trackedFrom
                            ? <> Event-based steps tracked from {new Date(data.trackedFrom).toLocaleDateString()}.</>
                            : <> Event-based steps start tracking with the first recorded event.</>}
                    </p>
                </div>
                <div className={funnelStyles.controls}>
                    <div className={styles.rangeGroup} role="group" aria-label="Time range">
                        {RANGES.map((r) => (
                            <button key={r.value} type="button" className={r.value === range ? styles.rangeBtnActive : styles.rangeBtn} aria-pressed={r.value === range} onClick={() => setRange(r.value)}>
                                {r.label}
                            </button>
                        ))}
                    </div>
                    <div className={styles.rangeGroup} role="group" aria-label="Breakdown">
                        {BREAKDOWNS.filter((b) => b.value !== 'source' || sourceAvailable).map((b) => (
                            <button key={b.value} type="button" className={b.value === by ? styles.rangeBtnActive : styles.rangeBtn} aria-pressed={b.value === by} onClick={() => setBy(b.value)}>
                                {b.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            <CohortFilterBar filters={filters} onChange={updateFilters} options={diag?.filterOptions} labels={diag?.filterLabels || undefined} />

            {error && <div className={styles.error}>{error}</div>}
            {loading && !data && <div className={styles.loading}>Loading activation funnel...</div>}

            {data && (
                <div className={`${styles.content} ${loading ? styles.contentLoading : ''}`}>
                    <section className={styles.section}>
                        <h2 className={styles.sectionTitle}>{by ? 'Overall' : 'Funnel'}</h2>
                        <FunnelTable funnel={data.funnel} />
                    </section>

                    {data.breakdown && (
                        <section className={styles.section}>
                            <h2 className={styles.sectionTitle}>By {BREAKDOWNS.find((b) => b.value === by)?.label?.toLowerCase()}</h2>
                            {data.breakdown.length === 0 && <div className={styles.empty}>No signups in this range.</div>}
                            {data.breakdown.map((row) => (
                                <div key={row.segment} className={funnelStyles.segment}>
                                    <h3 className={funnelStyles.segmentTitle}>
                                        {row.segment}
                                        {row.segment === 'untracked' && <span className={funnelStyles.tag}>signed up before tracking</span>}
                                    </h3>
                                    <FunnelTable funnel={row.funnel} compact />
                                </div>
                            ))}
                        </section>
                    )}

                    {diagError && <div className={styles.error}>{diagError}</div>}
                    {diag && (
                        <>
                            <section className={styles.section}>
                                <h2 className={styles.sectionTitle}>Onboarding detail: started → completed</h2>
                                <p className={styles.sectionNote}>
                                    {diag.trackedFrom
                                        ? <>Step-level diagnostics recorded from {new Date(diag.trackedFrom).toLocaleDateString()}; users on earlier app builds have no step data and are excluded from the step rows.</>
                                        : <>No step-level diagnostics recorded yet — they start with the first app / web build that includes them. Nothing below is back-filled.</>}
                                </p>
                                <DiagnosticSummaryCards summary={diag.summary} trackedFrom={diag.trackedFrom} />
                                <OnboardingStepFunnel data={diag} onDrill={setDrill} />
                            </section>

                            <section className={styles.section}>
                                <h2 className={styles.sectionTitle}>Title selection</h2>
                                <p className={styles.sectionNote}>Optional since onboarding version 2: users can continue with any number of picks or skip. Skipping is a way through the step, not abandonment.</p>
                                <TitleSelectionOutcomes outcomes={diag.titleSelection} onDrill={setDrill} />
                            </section>

                            <section className={styles.section}>
                                <h2 className={styles.sectionTitle}>Abandonment vs technical failure</h2>
                                <p className={styles.sectionNote}>Diagnostic classification of every signup in the cohort (click to list the users). Heuristics, not verdicts.</p>
                                <ClassificationSummary classifications={diag.classifications} onDrill={setDrill} />
                                {drill && (
                                    <DiagnosticUsersList filters={filters} range={range} drill={drill} onClose={() => setDrill(null)} onOpenUser={setJourneyUser} />
                                )}
                                {journeyUser && (
                                    <div className={funnelStyles.segment}>
                                        <h3 className={funnelStyles.segmentTitle}>
                                            Onboarding journey
                                            <button type="button" className={funnelStyles.tag} onClick={() => setJourneyUser(null)}>close</button>
                                        </h3>
                                        <OnboardingJourney userId={journeyUser} />
                                    </div>
                                )}
                            </section>

                            <section className={styles.section}>
                                <h2 className={styles.sectionTitle}>Technical errors</h2>
                                <p className={styles.sectionNote}>Grouped by step, operation, type, HTTP status and error code — the same failure across many users is one row.</p>
                                <ErrorSignatures errors={diag.errors} />
                            </section>

                            <section className={styles.section}>
                                <h2 className={styles.sectionTitle}>Android devices & builds</h2>
                                <p className={styles.sectionNote}>Onboarding starters on Android by the device / build they onboarded on. Segments under {diag.minSample} starters are flagged.</p>
                                <AndroidDeviceBreakdown devices={diag.devices} minSample={diag.minSample} />
                            </section>

                            <section className={styles.section}>
                                <h2 className={styles.sectionTitle}>Quiz handoff</h2>
                                <QuizHandoff handoff={diag.quizHandoff} />
                            </section>
                        </>
                    )}

                    <TopMoviesFunnel range={range} />
                    <RankingsFunnel range={range} />

                    <section className={styles.section}>
                        <h2 className={styles.sectionTitle}>How to read this</h2>
                        <ul className={funnelStyles.notes}>
                            {(data.notes || []).map((n, i) => <li key={i}>{n}</li>)}
                            {(diag?.notes || []).map((n, i) => <li key={`d${i}`}>{n}</li>)}
                            {!sourceAvailable && <li>Source breakdown appears once first-touch attribution has been captured for at least one signup.</li>}
                        </ul>
                    </section>
                </div>
            )}
        </div>
    );
}

function FunnelTable({ funnel = [], compact = false }) {
    const max = Math.max(1, ...funnel.map((s) => s.count));
    return (
        <table className={`${funnelStyles.table} ${compact ? funnelStyles.compact : ''}`}>
            <thead>
                <tr>
                    <th>Step</th>
                    <th className={funnelStyles.num}>Users</th>
                    <th className={funnelStyles.num}>Of signups</th>
                    <th className={funnelStyles.num}>Step conv.</th>
                    <th className={funnelStyles.num}>Drop-off</th>
                    <th className={funnelStyles.barCol} aria-hidden="true" />
                </tr>
            </thead>
            <tbody>
                {funnel.map((step) => (
                    <tr key={step.key}>
                        <td>{step.label}</td>
                        <td className={funnelStyles.num}>{step.count.toLocaleString()}</td>
                        <td className={funnelStyles.num}>{step.conversionFromSignup}%</td>
                        <td className={funnelStyles.num}>{step.conversionFromPrevious}%</td>
                        <td className={`${funnelStyles.num} ${step.dropOffFromPrevious > 50 ? funnelStyles.warn : ''}`}>{step.key === 'signups' ? '—' : `${step.dropOffFromPrevious}%`}</td>
                        <td className={funnelStyles.barCol}><div className={funnelStyles.bar} style={{ width: `${Math.round((step.count / max) * 100)}%` }} /></td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}
